import React, { useEffect, useState } from "react";
import {
  Search,
  Download,
  Printer,
  FileSpreadsheet,
  FileText,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Apiurl from "../environment/Apiurl";

const JobSheet_Report = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [error, setError] = useState(null);
  const [jobSheetData, setJobSheetData] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [staff, setStaff] = useState([]);

  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedServiceHome, setSelectedServiceHome] = useState("");
  const [selectStaff, setSelectStaff] = useState("");

  const todayDate = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(todayDate);
  const [toDate, setToDate] = useState("");

  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date)) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formatCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, "0");
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const year = String(today.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const fetchJobSheetData = async () => {
    try {
      const response = await axios.get(`${Apiurl}/job-sheet`);
      const sortedData = response.data;
      console.log("Job Sheet Data Received:", sortedData);
      setJobSheetData(sortedData || []);
      setError(null);
    } catch (error) {
      console.error("Error fetching job sheet data:", error.message);
      setError("Failed to fetch job sheet data. Please try again.");
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${Apiurl}/brand`);
      setBrands(response.data || []);
    } catch (error) {
      console.error("Error fetching brands:", error.message);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${Apiurl}/model`);
      setModels(response.data || []);
    } catch (error) {
      console.error("Error fetching models:", error.message);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${Apiurl}/service`);
      setServiceTypes(response.data || []);
    } catch (error) {
      console.error("Error fetching service types:", error.message);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${Apiurl}/staff`);
      const data = response.data || [];
      const filterStaff = data.filter(
        (item) => item.role === "Customer Care Officer",
      );
      setStaff(filterStaff);
    } catch (error) {
      console.error("Error fetching service types:", error.message);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    fetchJobSheetData();
    fetchBrands();
    fetchModels();
    fetchServiceTypes();
    fetchStaff();
  }, []);

  const getFilteredData = () => {
    let filteredData = [...jobSheetData];

    if (searchTerm) {
      filteredData = filteredData.filter((item) =>
        [
          item.sheet_no,
          item.customer_name,
          item.mobile_number,
          item.mobile_brand,
          item.mobile_model,
          item.customer_care_officer,
          item.apperance_status,
          item.warranty_status,
          ...(item.complaint_details?.flatMap((c) => [
            c.complaint_name,
            c.complaint_no,
            c.complaint_notes,
          ]) || []),
        ]
          .filter(Boolean)
          .some((value) =>
            value.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
      );
    }

    if (selectedBrand)
      filteredData = filteredData.filter(
        (item) => item.mobile_brand === selectedBrand,
      );
    if (selectedModel)
      filteredData = filteredData.filter(
        (item) => item.mobile_model === selectedModel,
      );
    if (selectedServiceHome) {
      filteredData = filteredData.filter((item) =>
        item.complaint_details?.some(
          (c) => c.complaint_name === selectedServiceHome,
        ),
      );
    }

    if (selectStaff) {
      filteredData = filteredData.filter(
        (item) =>
          item.customer_care_officer?.toLowerCase() ===
          selectStaff.toLowerCase(),
      );
    }

    if (fromDate && toDate) {
      filteredData = filteredData.filter((item) => {
        const itemDate = new Date(item.date).toISOString().split("T")[0];
        return itemDate >= fromDate && itemDate <= toDate;
      });
    }

    return filteredData;
  };

  const getPaginatedData = () => {
    const filteredData = getFilteredData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = () =>
    Math.ceil(getFilteredData().length / itemsPerPage) || 1;

  // ==================== TABLE RENDER ====================
  const renderTable = () => {
    const paginatedData = getPaginatedData();

    return (
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr style={{ backgroundColor: "#2e9290", color: "white" }}>
              <th>S.No</th>
              <th>Sheet No</th>
              <th>Date</th>
              <th>Time</th>
              <th>Customer Name</th>
              <th>Brand</th>
              <th>Model</th>
              <th>CCO</th>
              <th>Complaint Name / Complaint No</th>
              <th>Warranty Status</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={10} className="text-center">
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.flatMap((item, itemIndex) => {
                const complaints = item.complaint_details || [];
                const rows = [];

                if (complaints.length > 0) {
                  complaints.forEach((complaint, compIndex) => {
                    rows.push(
                      <tr
                        key={`${item.sheet_no}-${compIndex}`}
                        className="align-middle"
                      >
                        <td>{compIndex === 0 ? itemIndex + 1 : ""}</td>
                        <td>{compIndex === 0 ? item.sheet_no : ""}</td>
                        <td>{compIndex === 0 ? formatDate(item.date) : ""}</td>
                        <td>{compIndex === 0 ? item.time || "" : ""}</td>
                        <td>{compIndex === 0 ? item.customer_name : ""}</td>
                        <td>{compIndex === 0 ? item.mobile_brand : ""}</td>
                        <td>{compIndex === 0 ? item.mobile_model : ""}</td>
                        <td>
                          {compIndex === 0 ? item.customer_care_officer : ""}
                        </td>
                        <td>
                          <div>
                            <span>
                              {complaint.complaint_name} /{" "}
                              {complaint.complaint_no}
                            </span>
                          </div>
                          <div>
                            <span className="fw-semibold">Service Notes :</span>{" "}
                            {complaint.complaint_notes || "N/A"}
                          </div>
                        </td>
                        <td>{compIndex === 0 ? item.warranty_status : ""}</td>
                      </tr>,
                    );
                  });

                  // Appearance Status Row
                  rows.push(
                    <tr
                      key={`${item.sheet_no}-appearance`}
                      style={{ backgroundColor: "#f8f9fa" }}
                    >
                      <td colSpan={8}></td>
                      <td>
                        <span className="fw-semibold">Appearance Status :</span>{" "}
                        {item.apperance_status || "N/A"}
                      </td>
                      {/* <td></td> */}
                    </tr>,
                  );
                } else {
                  rows.push(
                    <tr key={item.sheet_no}>
                      <td>{itemIndex + 1}</td>
                      <td>{item.sheet_no}</td>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.time || ""}</td>
                      <td>{item.customer_name}</td>
                      <td>{item.mobile_brand}</td>
                      <td>{item.mobile_model}</td>
                      <td>{item.customer_care_officer}</td>
                      <td>No Complaints</td>
                      <td>{item.warranty_status}</td>
                    </tr>,
                  );
                  rows.push(
                    <tr
                      key={`${item.sheet_no}-appearance`}
                      style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}
                    >
                      <td colSpan={10}>
                        Appearance Status : {item.apperance_status || "N/A"}
                      </td>
                    </tr>,
                  );
                }
                return rows;
              })
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // ==================== DOWNLOAD FUNCTIONS ====================

  const downloadCSV = () => {
    const filteredData = getFilteredData();
    let csvContent =
      "S.No,Sheet No,Date,Time,Customer Name,Brand,Model,CCO,Complaint Name / No,Service Notes,Warranty Status,Appearance Status\n";

    filteredData.forEach((item, index) => {
      const sNo = index + 1;
      if (item.complaint_details?.length > 0) {
        item.complaint_details.forEach((complaint, cIndex) => {
          const row = [
            cIndex === 0 ? sNo : "",
            cIndex === 0 ? `"${item.sheet_no || "N/A"}"` : "",
            cIndex === 0 ? `"${formatDate(item.date)}"` : "",
            cIndex === 0 ? `"${item.time || ""}"` : "",
            cIndex === 0 ? `"${item.customer_name || "N/A"}"` : "",
            cIndex === 0 ? `"${item.mobile_brand || "N/A"}"` : "",
            cIndex === 0 ? `"${item.mobile_model || "N/A"}"` : "",
            cIndex === 0 ? `"${item.customer_care_officer || "N/A"}"` : "",
            `"${complaint.complaint_name || ""} / ${complaint.complaint_no || ""}"`,
            `"${complaint.complaint_notes || "N/A"}"`,
            cIndex === 0 ? `"${item.warranty_status || "N/A"}"` : "",
            cIndex === 0 ? `"${item.apperance_status || "N/A"}"` : "",
          ].join(",");
          csvContent += row + "\n";
        });
      }
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "job_sheet_report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    const filteredData = getFilteredData();
    let excelContent = `<table border="1">
      <tr>
        <th>S.No</th><th>Sheet No</th><th>Date</th><th>Time</th><th>Customer Name</th>
        <th>Brand</th><th>Model</th><th>CCO</th><th>Complaint Name / No</th><th>Service Notes</th>
        <th>Warranty Status</th><th>Appearance Status</th>
      </tr>`;

    filteredData.forEach((item, index) => {
      if (item.complaint_details?.length > 0) {
        item.complaint_details.forEach((complaint, cIndex) => {
          excelContent += `<tr>
            <td>${cIndex === 0 ? index + 1 : ""}</td>
            <td>${cIndex === 0 ? item.sheet_no : ""}</td>
            <td>${cIndex === 0 ? formatDate(item.date) : ""}</td>
            <td>${cIndex === 0 ? item.time || "" : ""}</td>
            <td>${cIndex === 0 ? item.customer_name : ""}</td>
            <td>${cIndex === 0 ? item.mobile_brand : ""}</td>
            <td>${cIndex === 0 ? item.mobile_model : ""}</td>
            <td>${cIndex === 0 ? item.customer_care_officer : ""}</td>
            <td>${complaint.complaint_name} / ${complaint.complaint_no}</td>
            <td>${complaint.complaint_notes || "N/A"}</td>
            <td>${cIndex === 0 ? item.warranty_status : ""}</td>
            <td>${cIndex === 0 ? item.apperance_status : ""}</td>
          </tr>`;
        });
      }
    });

    excelContent += "</table>";

    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "job_sheet_report.xls";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const filteredData = getFilteredData();
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;

    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("WE CARE", pageWidth / 2, 15, { align: "center" });

    doc.setFontSize(10);
    doc.text(
      "NO-113 & 114, Lighthouse Complex, Omalur Main Road, 4 Roads, Salem - 636009",
      pageWidth / 2,
      22,
      { align: "center" },
    );

    doc.setFontSize(12);
    doc.text(
      `Job Sheet Report From ${fromDate ? formatDate(fromDate) : formatCurrentDate()} to ${toDate ? formatDate(toDate) : formatCurrentDate()}`,
      pageWidth / 2,
      30,
      { align: "center" },
    );

    const headers = [
      "S.No",
      "Sheet No",
      "Date",
      "Time",
      "Customer",
      "Brand",
      "Model",
      "CCO",
      "Complaint Details",
      "Warranty",
    ];

    const bodyRows = [];

    filteredData.forEach((item, index) => {
      const complaints = item.complaint_details || [];

      if (complaints.length > 0) {
        complaints.forEach((complaint, cIndex) => {
          bodyRows.push([
            cIndex === 0 ? index + 1 : "",
            cIndex === 0 ? item.sheet_no : "",
            cIndex === 0 ? formatDate(item.date) : "",
            cIndex === 0 ? item.time || "" : "",
            cIndex === 0 ? item.customer_name : "",
            cIndex === 0 ? item.mobile_brand : "",
            cIndex === 0 ? item.mobile_model : "",
            cIndex === 0 ? item.customer_care_officer : "",
            `${complaint.complaint_name} / ${complaint.complaint_no}
Service Notes: ${complaint.complaint_notes || "N/A"}`,
            cIndex === 0 ? item.warranty_status : "",
          ]);
        });

        // Appearance Status Row
        bodyRows.push([
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          `Appearance Status: ${item.apperance_status || "N/A"}`,
          "",
        ]);
      } else {
        bodyRows.push([
          index + 1,
          item.sheet_no,
          formatDate(item.date),
          item.time || "",
          item.customer_name,
          item.mobile_brand,
          item.mobile_model,
          item.customer_care_officer,
          "No Complaints",
          item.warranty_status,
        ]);

        bodyRows.push([
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          `Appearance Status : ${item.apperance_status || "N/A"}`,
          "",
        ]);
      }
    });

    autoTable(doc, {
      startY: 35,
      head: [headers],
      body: bodyRows,

      theme: "plain",

      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        valign: "middle",
        lineWidth: 0,
      },

      headStyles: {
        fillColor: [46, 146, 144],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        lineWidth: 0,
      },

      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
    });

    doc.save("job_sheet_report.pdf");
  };

  const handlePrint = () => {
    const filteredData = getFilteredData();
    const printWindow = window.open("", "_self");

    let printContent = `
      <html>
        <head>
          <title>Job Sheet Report</title>
          <link
    href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css"
    rel="stylesheet"
  />
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h2, h4 { text-align: center; }
            .address { text-align: center; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #000; padding: 5px; text-align: left; vertical-align: top; }
            th { background-color: #2e9290; color: white; }
            .appearance-row { background-color: #f8f9fa; font-weight: bold; }
            @media print { button { display: none; } }
          </style>
        </head>
        <body>
          <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-success btn-sm" onclick="window.print()">Print</button>
          <button class="btn btn-secondary btn-sm" onclick="window.location.href='/job-sheet-report'">Back</button>
          </div>
          <h2>WE CARE</h2>
          <p class="address">NO-113 & 114, Lighthouse Complex, Omalur Main Road, 4 Roads, Salem - 636009</p>
          <h4>Job Sheet Report</h4>
          <table>
            <thead>
              <tr>
                <th>S.No</th><th>Sheet No</th><th>Date</th><th>Time</th><th>Customer Name</th>
                <th>Brand</th><th>Model</th><th>CCO</th><th>Complaint Name / No</th><th>Warranty</th>
              </tr>
            </thead>
            <tbody>
    `;

    filteredData.forEach((item, index) => {
      if (item.complaint_details?.length > 0) {
        item.complaint_details.forEach((complaint, cIndex) => {
          printContent += `
            <tr>
              <td>${cIndex === 0 ? index + 1 : ""}</td>
              <td>${cIndex === 0 ? item.sheet_no : ""}</td>
              <td>${cIndex === 0 ? formatDate(item.date) : ""}</td>
              <td>${cIndex === 0 ? item.time || "" : ""}</td>
              <td>${cIndex === 0 ? item.customer_name : ""}</td>
              <td>${cIndex === 0 ? item.mobile_brand : ""}</td>
              <td>${cIndex === 0 ? item.mobile_model : ""}</td>
              <td>${cIndex === 0 ? item.customer_care_officer : ""}</td>
              <td><strong>${complaint.complaint_name} / ${complaint.complaint_no}</strong> <br/> <br/><strong>Service Notes :</strong> ${complaint.complaint_notes || "N/A"}</td>
              <td>${cIndex === 0 ? item.warranty_status : ""}</td>
            </tr>
          `;
        });
        printContent += `
          <tr class="appearance-row">
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
            <td colspan="2">Appearance Status : ${item.apperance_status || "N/A"}</td>
          </tr>
        `;
      }
    });

    printContent += `</tbody></table></body></html>`;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const renderPagination = () => {
    const totalPages = getTotalPages();
    if (totalPages <= 1 && itemsPerPage !== Number.MAX_SAFE_INTEGER)
      return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible)
      startPage = Math.max(1, endPage - maxVisible + 1);

    for (let i = startPage; i <= endPage; i++) pages.push(i);

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, getFilteredData().length)} of{" "}
          {getFilteredData().length} entries
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          {pages.map((page) => (
            <button
              key={page}
              className={`btn btn-sm ${currentPage === page ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className="container-fluid p-3"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card card-body mt-2">
        <div
          className="card-header d-flex justify-content-between align-items-center p-3"
          style={{ backgroundColor: "#2e9290", color: "white" }}
        >
          <h5 className="mb-0">Job Sheet Report</h5>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <span className="d-flex flex-column">
              <label className="text-white">Brand</label>
              <select
                className="form-select form-select-sm"
                value={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                {brands.map((brand, i) => (
                  <option key={i} value={brand.brand_name}>
                    {brand.brand_name}
                  </option>
                ))}
              </select>
            </span>

            <span className="d-flex flex-column">
              <label className="text-white">Model</label>
              <select
                className="form-select form-select-sm"
                value={selectedModel}
                onChange={(e) => {
                  setSelectedModel(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                {models.map((model, i) => (
                  <option key={i} value={model.model_name}>
                    {model.model_name}
                  </option>
                ))}
              </select>
            </span>

            <span className="d-flex flex-column">
              <label className="text-white">Complaint Type</label>
              <select
                className="form-select form-select-sm"
                value={selectedServiceHome}
                onChange={(e) => {
                  setSelectedServiceHome(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                {serviceTypes.map((service, i) => (
                  <option key={i} value={service.serviceName}>
                    {service.serviceName}
                  </option>
                ))}
              </select>
            </span>

            <span className="d-flex flex-column">
              <label className="text-white">CCO</label>
              <select
                className="form-select form-select-sm"
                value={selectStaff}
                onChange={(e) => {
                  setSelectStaff(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                {staff.map((staff, i) => (
                  <option key={i} value={staff.full_name}>
                    {staff.full_name}
                  </option>
                ))}
              </select>
            </span>

            <span className="d-flex flex-column mt-4">
              <div className="d-flex gap-2">
                <div className="d-flex flex-column">
                  <label className="text-white small">From Date:</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <div className="d-flex flex-column">
                  <label className="text-white small">To Date:</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate}
                    onChange={(e) => {
                      setToDate(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </span>

            <button
              className="btn btn-outline-light btn-sm mt-4"
              onClick={() => {
                setSelectedBrand("");
                setSelectedModel("");
                setSelectedServiceHome("");
                setSelectStaff("");
                setFromDate("");
                setToDate("");
                setCurrentPage(1);
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 align-items-center">
            {/* Show Entries */}
            <div className="col-md-3 mb-2">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={
                    itemsPerPage === Number.MAX_SAFE_INTEGER
                      ? "All"
                      : itemsPerPage
                  }
                  onChange={(e) => {
                    setItemsPerPage(
                      e.target.value === "All"
                        ? Number.MAX_SAFE_INTEGER
                        : Number(e.target.value),
                    );
                    setCurrentPage(1);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="col-md-6 mb-2">
              <div className="d-flex flex-wrap gap-2 justify-content-center">
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadPDF}
                >
                  <FileText size={16} /> PDF
                </button>

                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadExcel}
                >
                  <FileSpreadsheet size={16} /> Excel
                </button>

                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadCSV}
                >
                  <Download size={16} /> CSV
                </button>

                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={handlePrint}
                >
                  <Printer size={16} /> Print
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="col-md-3 mb-2">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm pe-5"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <Search
                  size={16}
                  className="position-absolute text-muted"
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </div>
            </div>
          </div>

          {renderTable()}
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default JobSheet_Report;
